import type { ReactNode } from 'react';
import { useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { Box, FormInput, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import { SkillExecutionMode } from '../types';

interface SkillFormValues {
  name: string;
  description: string;
  promptSnippet: string;
  category: string;
  toolNames: string[];
  executionMode: SkillExecutionMode;
  maxToolCalls: number;
}

interface CreateSkillFormProps {
  initialValues?: Partial<SkillFormValues>;
  availableTools?: string[];
  onSubmit: (values: SkillFormValues) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  mode: 'create' | 'edit';
}

function FormField({
  label,
  children,
  counter,
}: Readonly<{
  label: string;
  children: ReactNode;
  counter?: string;
}>) {
  return (
    <VStack spacing="xs">
      {counter ? (
        <HStack spacing="sm" align="center" justify="between">
          <Text variant="caption" color={colors.text.secondary} style={fieldStyles.label}>
            {label}
          </Text>
          <Text variant="caption" color={colors.text.tertiary}>
            {counter}
          </Text>
        </HStack>
      ) : (
        <Text variant="caption" color={colors.text.secondary} style={fieldStyles.label}>
          {label}
        </Text>
      )}
      {children}
    </VStack>
  );
}

const fieldStyles = StyleSheet.create({
  label: { fontWeight: '600' },
});

const EXECUTION_MODE_OPTIONS = [
  { value: SkillExecutionMode.PROMPT_ONLY, labelKey: 'agents.createSkill.modePromptOnly' },
  { value: SkillExecutionMode.AUTO_EXECUTE, labelKey: 'agents.createSkill.modeAutoExecute' },
  {
    value: SkillExecutionMode.REQUIRE_APPROVAL,
    labelKey: 'agents.createSkill.modeRequireApproval',
  },
];

export function CreateSkillForm({
  initialValues,
  availableTools = [],
  onSubmit,
  onCancel,
  isSubmitting,
  mode,
}: Readonly<CreateSkillFormProps>) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialValues?.name ?? '');
  const [description, setDescription] = useState(initialValues?.description ?? '');
  const [promptSnippet, setPromptSnippet] = useState(initialValues?.promptSnippet ?? '');
  const [category, setCategory] = useState(initialValues?.category ?? '');
  const [selectedTools, setSelectedTools] = useState<string[]>(initialValues?.toolNames ?? []);
  const [executionMode, setExecutionMode] = useState<SkillExecutionMode>(
    initialValues?.executionMode ?? SkillExecutionMode.PROMPT_ONLY,
  );
  const [maxToolCalls, setMaxToolCalls] = useState(initialValues?.maxToolCalls ?? 5);

  const isValid =
    name.trim().length >= 3 &&
    description.trim().length >= 10 &&
    promptSnippet.trim().length >= 10 &&
    category.trim().length >= 2;

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      promptSnippet: promptSnippet.trim(),
      category: category.trim().toLowerCase().replaceAll(/\s+/g, '_'),
      toolNames: selectedTools,
      executionMode,
      maxToolCalls,
    });
  };

  const toggleTool = (toolName: string) => {
    setSelectedTools((prev) =>
      prev.includes(toolName) ? prev.filter((t) => t !== toolName) : [...prev, toolName],
    );
  };

  return (
    <VStack spacing="md">
      <Text variant="bodySmall" color={colors.text.primary} style={styles.title}>
        {mode === 'create'
          ? t('agents.createSkill.createTitle')
          : t('agents.createSkill.editTitle')}
      </Text>

      <FormField label={t('agents.createSkill.nameLabel')}>
        <Box borderRadius="md" style={styles.inputContainer}>
          <FormInput
            value={name}
            onChangeText={(text) => setName(text.slice(0, 100))}
            placeholder={t('agents.createSkill.namePlaceholder')}
            style={styles.input}
          />
        </Box>
      </FormField>

      <FormField label={t('agents.createSkill.categoryLabel')}>
        <Box borderRadius="md" style={styles.inputContainer}>
          <FormInput
            value={category}
            onChangeText={(text) => setCategory(text.slice(0, 50))}
            placeholder={t('agents.createSkill.categoryPlaceholder')}
            style={styles.input}
          />
        </Box>
      </FormField>

      <FormField
        label={t('agents.createSkill.descriptionLabel')}
        counter={`${description.length}/500`}
      >
        <Box borderRadius="md" style={styles.inputContainer}>
          <FormInput
            value={description}
            onChangeText={(text) => setDescription(text.slice(0, 500))}
            multiline
            placeholder={t('agents.createSkill.descriptionPlaceholder')}
            style={[styles.input, styles.multiline]}
            textAlignVertical="top"
          />
        </Box>
      </FormField>

      <FormField
        label={t('agents.createSkill.promptLabel')}
        counter={`${promptSnippet.length}/5000`}
      >
        <Text variant="caption" color={colors.text.tertiary} style={styles.hint}>
          {t('agents.createSkill.promptHint')}
        </Text>
        <Box borderRadius="md" style={styles.inputContainer}>
          <FormInput
            value={promptSnippet}
            onChangeText={(text) => setPromptSnippet(text.slice(0, 5000))}
            multiline
            placeholder={t('agents.createSkill.promptPlaceholder')}
            style={[styles.input, styles.promptEditor]}
            textAlignVertical="top"
          />
        </Box>
      </FormField>

      <FormField label={t('agents.createSkill.executionModeLabel')}>
        <HStack spacing="xs">
          {EXECUTION_MODE_OPTIONS.map((opt) => (
            <Box
              key={opt.value}
              pressable
              onPress={() => setExecutionMode(opt.value)}
              borderRadius="md"
              padding="xs"
              backgroundColor={
                executionMode === opt.value ? colors.primary.muted : colors.background.tertiary
              }
              style={[styles.modeChip, executionMode === opt.value && styles.modeChipActive]}
            >
              <Text
                variant="caption"
                color={executionMode === opt.value ? colors.primary[400] : colors.text.tertiary}
                style={styles.modeChipText}
              >
                {t(opt.labelKey)}
              </Text>
            </Box>
          ))}
        </HStack>
      </FormField>

      {availableTools.length > 0 && (
        <FormField label={t('agents.createSkill.toolsLabel')}>
          <Text variant="caption" color={colors.text.tertiary} style={styles.hint}>
            {t('agents.createSkill.toolsHint')}
          </Text>
          <ScrollView horizontal={false} style={styles.toolsList} nestedScrollEnabled>
            <HStack spacing="xs" style={styles.toolsWrap}>
              {availableTools.map((toolName) => (
                <Box
                  key={toolName}
                  pressable
                  onPress={() => toggleTool(toolName)}
                  borderRadius="md"
                  padding="xs"
                  backgroundColor={
                    selectedTools.includes(toolName)
                      ? colors.primary.muted
                      : colors.background.tertiary
                  }
                  style={[
                    styles.toolChip,
                    selectedTools.includes(toolName) && styles.toolChipActive,
                  ]}
                >
                  <Text
                    variant="caption"
                    color={
                      selectedTools.includes(toolName) ? colors.primary[400] : colors.text.tertiary
                    }
                    style={styles.toolChipText}
                  >
                    {toolName.replaceAll('_', ' ')}
                  </Text>
                </Box>
              ))}
            </HStack>
          </ScrollView>
        </FormField>
      )}

      {executionMode !== SkillExecutionMode.PROMPT_ONLY && (
        <FormField label={t('agents.createSkill.maxToolCallsLabel')}>
          <HStack spacing="sm" align="center">
            <Box
              pressable
              onPress={() => setMaxToolCalls(Math.max(1, maxToolCalls - 1))}
              borderRadius="full"
              alignItems="center"
              justifyContent="center"
              style={styles.stepperButton}
            >
              <Text variant="bodySmall" color={colors.text.primary}>
                -
              </Text>
            </Box>
            <Text variant="bodySmall" color={colors.text.primary} style={styles.stepperValue}>
              {maxToolCalls}
            </Text>
            <Box
              pressable
              onPress={() => setMaxToolCalls(Math.min(10, maxToolCalls + 1))}
              borderRadius="full"
              alignItems="center"
              justifyContent="center"
              style={styles.stepperButton}
            >
              <Text variant="bodySmall" color={colors.text.primary}>
                +
              </Text>
            </Box>
          </HStack>
        </FormField>
      )}

      <HStack spacing="sm" justify="between">
        <Button variant="ghost" size="sm" onPress={onCancel}>
          {t('common.actions.cancel')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onPress={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting && t('agents.createSkill.saving')}
          {!isSubmitting && mode === 'create' && t('agents.createSkill.createSkill')}
          {!isSubmitting && mode !== 'create' && t('agents.createSkill.saveChanges')}
        </Button>
      </HStack>
    </VStack>
  );
}

const styles = StyleSheet.create({
  title: {
    fontWeight: '700',
  },
  hint: {
    fontSize: tokens.fontSize.xs,
    lineHeight: 15,
  },
  inputContainer: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.background.secondary,
  },
  input: {
    padding: tokens.spacing.smPlus,
    color: colors.text.primary,
    fontSize: tokens.fontSize.smPlus,
    lineHeight: 18,
  },
  multiline: {
    minHeight: 60,
    maxHeight: 120,
  },
  promptEditor: {
    minHeight: 100,
    maxHeight: 250,
    fontFamily: darkPrecisionFontFamilies.metric,
  },
  modeChip: {
    borderWidth: 1,
    borderColor: 'transparent',
    paddingHorizontal: tokens.spacing.smPlus,
    paddingVertical: 6,
  },
  modeChipActive: {
    borderColor: colors.primary.subtle,
  },
  modeChipText: {
    fontSize: tokens.fontSize.xs,
    fontWeight: '500',
  },
  toolsList: {
    maxHeight: 120,
  },
  toolsWrap: {
    flexWrap: 'wrap',
    gap: 6,
  },
  toolChip: {
    borderWidth: 1,
    borderColor: 'transparent',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  toolChipActive: {
    borderColor: colors.primary.subtle,
  },
  toolChipText: {
    fontSize: tokens.fontSize.xs,
    fontWeight: '500',
  },
  stepperButton: {
    width: 28,
    height: 28,
    backgroundColor: colors.background.tertiary,
    borderWidth: 1,
    borderColor: colors.border.subtle,
  },
  stepperValue: {
    fontWeight: '600',
    minWidth: 24,
    textAlign: 'center',
  },
});
